import { useState } from 'react';
import type { ReportNotesKey } from '@/core/models/firm-settings.models';
import { Icon } from '@/kit/Icon';
import { TabGroup } from '@/kit/Tabs';
import { FirmNoteTexts } from './FirmNoteTexts';
import { Tooltip } from '@/kit/Tooltip';
import { LimitedCompanyMenu } from './LimitedCompanyMenu';
import {
  AccountantAuditorTab,
  PolicyTab,
  ReportPointsTab,
  incorporatedLayout,
  useReportNotesSettings,
} from './ReportNotesSettingsBase';

/**
 * Layout of every limited company screen - UK-GAAP, FRS 102, FRS 102 Section 1A and FRS 105 (must match the API layout for the
 * key). AP parity: the 3.0 screen shows one shared Accounting Policy list for every taxonomy - 18 P&L and 15 balance sheet
 * policies, with Goodwill .. Computer software development costs (balance sheet policies 2-5) indented under Intangible assets.
 */
const LAYOUT = incorporatedLayout(
  {
    reportPoints: 11,
    accountantReport: 5,
    auditorReport: 5,
    profitAndLossPolicies: 18,
    balanceSheetPolicies: 15,
  },
  ['bsPolicy2', 'bsPolicy3', 'bsPolicy4', 'bsPolicy5'],
);

export interface LimitedCompanyScreenProps {
  settingsKey: ReportNotesKey;
  /** Lightbulb text; only the UK-GAAP screen has one (and the `emoji-sec pt-5` frame that goes with it). */
  tooltip?: string;
  /** The source screens differ only in these paddings (FRS102 has none, UK-GAAP and FRS105 have `pt-10`). */
  padded: boolean;
}

/** The body shared by LimitedcompanyComponent / FRS102 / FRS105: the framework menu plus the three tabs. */
export function LimitedCompanyScreen({ settingsKey, tooltip, padded }: LimitedCompanyScreenProps) {
  const notes = useReportNotesSettings(settingsKey, LAYOUT);
  const [istoggleSideList, setIstoggleSideList] = useState(false);
  const pt = padded ? ' pt-10' : '';

  return (
    <div className="sidemenu-hide">
      <div className="d-flex h-100">
        <LimitedCompanyMenu onToggleSideList={() => setIstoggleSideList((hidden) => !hidden)} />
        <div className={`form wp-100 ${istoggleSideList ? 'hide-sidelist' : ''}`.trim()}>
          <div className={`sidenav-details${tooltip ? ' emoji-sec pt-5' : ''}`}>
            {tooltip && (
              <div className="highlight-details">
                <Tooltip title={tooltip}>
                  <Icon name="emoji_objects" className="text-yellow-200 highlight-icon" />
                </Tooltip>
              </div>
            )}
            <div className="sidenav-body">
              <TabGroup
                alignTabs="start"
                tabs={[
                  {
                    label: "Director's Report",
                    children: (
                      <ReportPointsTab
                        notes={notes}
                        heading="Director's Report Points"
                        bodyClassName={`sidenav-body${pt}`}
                        separatorAfter={6}
                      />
                    ),
                  },
                  {
                    label: "Accountant / Auditor's Report",
                    children: <AccountantAuditorTab notes={notes} rowClassName={`d-flex gap-30${pt}`} />,
                  },
                  {
                    label: 'Accounting Policy',
                    children: <PolicyTab notes={notes} rowClassName={`d-flex gap-30${pt}`} />,
                  },
                  { label: 'Default Note Text', children: <FirmNoteTexts settingsKey={settingsKey} /> },
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
